<script setup>
import { ref, computed, onBeforeUnmount, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { vOnClickOutside } from '@vueuse/components';
import Button from 'dashboard/components-next/button/Button.vue';
import FilterInput from 'dashboard/components/widgets/FilterInput/Index.vue';

const props = defineProps({
  filterTypes: { type: Array, default: () => [] },
  appliedFilters: { type: Array, default: () => [] },
});

const emit = defineEmits(['applyFilter', 'clearFilters', 'close']);

const { t } = useI18n();

// Transform filterTypes to match FilterInput expected format
const transformedFilterTypes = computed(() => {
  return props.filterTypes.map(type => ({
    key: type.attributeKey,
    name: t(`ORDERS_LAYOUT.FILTER.ATTRIBUTES.${type.attributeI18nKey}`),
    inputType: type.inputType,
    filterOperators: type.filterOperators,
    options: type.options || [],
  }));
});

const DEFAULT_FILTER = {
  attribute_key: 'external_order_id',
  filter_operator: 'equal_to',
  values: '',
  query_operator: 'and',
};

const filters = ref([{ ...DEFAULT_FILTER }]);

const resetFilter = () => {
  filters.value = [{ ...DEFAULT_FILTER }];
};

const normalizeFilterValues = filter => {
  try {
    const def = props.filterTypes.find(
      f => f.attributeKey === filter.attribute_key
    );
    if (!def) {
      return filter;
    }

    const isMulti =
      def.inputType === 'multi_select' || def.inputType === 'search_select';
    if (isMulti) {
      const options = def.options || [];
      let raw = [];
      if (Array.isArray(filter.values)) {
        raw = filter.values;
      } else if (filter.values) {
        raw = [filter.values];
      }

      if (!raw.length) {
        filter.values = [];
        return filter;
      }

      // If already array of option objects, leave as-is
      if (typeof raw[0] === 'object') {
        return filter;
      }

      // Map ids to option objects
      const idSet = raw.map(v => String(v));
      filter.values = options
        .filter(opt => idSet.includes(String(opt.id)))
        .map(opt => ({ id: opt.id, name: opt.name }));
      return filter;
    }

    // For plain text/date keep as-is
    return filter;
  } catch (_) {
    return filter;
  }
};

// Restore filters from applied filters
const restoreFilters = () => {
  if (props.appliedFilters && props.appliedFilters.length > 0) {
    const restored = props.appliedFilters.map(f => ({ ...f }));
    filters.value = restored.map(normalizeFilterValues);
  } else {
    resetFilter();
  }
};

const removeFilter = index => {
  if (filters.value.length === 1) {
    resetFilter();
  } else {
    filters.value.splice(index, 1);
  }
};

const addFilter = () => {
  filters.value.push({ ...DEFAULT_FILTER });
};

const validateAndSubmit = () => {
  // Simple validation - check if values are not empty
  const validFilters = filters.value.filter(filter => {
    if (!filter.values) return false;

    if (typeof filter.values === 'string') {
      return filter.values.trim();
    }

    if (Array.isArray(filter.values)) {
      return filter.values.length > 0;
    }

    return !!filter.values;
  });

  if (validFilters.length === 0) return;

  emit('applyFilter', validFilters);
};

const clearFilters = () => {
  emit('clearFilters');
  emit('close');
  resetFilter();
};

const updateFilter = (index, filterData) => {
  filters.value[index] = { ...filters.value[index], ...filterData };
};

// Get operators for current filter type
const getOperators = attributeKey => {
  const filterType = props.filterTypes.find(
    type => type.attributeKey === attributeKey
  );
  return (
    filterType?.filterOperators || [
      { value: 'equal_to', label: 'Equal to' },
      { value: 'contains', label: 'Contains' },
    ]
  );
};

// Get dropdown options for multi-select filters
const getDropdownOptions = attributeKey => {
  const filterType = props.filterTypes.find(
    type => type.attributeKey === attributeKey
  );
  return filterType?.options || [];
};

onMounted(() => {
  restoreFilters();
});

watch(
  () => props.appliedFilters,
  () => restoreFilters(),
  { deep: true }
);

onBeforeUnmount(() => emit('close'));
const outsideClickHandler = [
  () => emit('close'),
  { ignore: ['#toggleOrdersFilterButton'] },
];
</script>

<template>
  <div
    v-on-click-outside="outsideClickHandler"
    class="z-40 max-w-3xl lg:w-[750px] overflow-visible w-full border border-n-weak bg-n-alpha-3 backdrop-blur-[100px] shadow-lg rounded-xl p-6 grid gap-6"
  >
    <h3 class="text-base font-medium leading-6 text-n-slate-12">
      {{ t('ORDERS_LAYOUT.FILTER.TITLE') }}
    </h3>

    <div class="grid gap-4">
      <div v-for="(filter, index) in filters" :key="index">
        <FilterInput
          :model-value="filter"
          :filter-attributes="transformedFilterTypes"
          :operators="getOperators(filter.attribute_key)"
          :dropdown-values="getDropdownOptions(filter.attribute_key)"
          :show-query-operator="index > 0"
          @update:modelValue="updateFilter(index, $event)"
          @removeFilter="removeFilter(index)"
        />
      </div>
    </div>

    <div class="flex items-center gap-2">
      <Button
        variant="outline"
        size="sm"
        icon="i-lucide-plus"
        @click="addFilter"
      >
        {{ t('ORDERS_LAYOUT.FILTER.ADD_FILTER') }}
      </Button>
    </div>

    <div class="flex items-center gap-2 pt-4 border-t border-n-weak">
      <Button variant="solid" size="sm" @click="validateAndSubmit">
        {{ t('ORDERS_LAYOUT.FILTER.APPLY_FILTERS') }}
      </Button>
      <Button variant="outline" size="sm" @click="clearFilters">
        {{ t('ORDERS_LAYOUT.FILTER.CLEAR_FILTERS') }}
      </Button>
    </div>
  </div>
</template>
